/**
 * Phase 27.36 — Prospect drawer.
 *
 * Opened from the Event Workspace Audience tab when the manager
 * clicks the "Prospect" badge (or the row) of a participant whose
 * NPN wasn't recognised at registration time — so was captured in
 * the `prospects` collection for recruiting follow-up.
 *
 * Shows the prospect's data (NPN, agency, source event, notes) and
 * exposes a one-click "Contact for recruiting" action that:
 *   • flips the prospect status to `contacted`
 *   • appends an internal note stamped with the acting user + event
 *   • is fully surfaced back on the /prospects section (same PATCH
 *     endpoint the Prospects page uses, so notes/status stay in sync)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Loader2, Sparkles, Phone, Mail, Building2, Hash, Calendar,
  ExternalLink, MessageSquarePlus, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { prospectAPI } from '@/lib/api';
import { getErrorMessage, formatPhone } from '@/lib/utils';
import ContactSmsCard from '@/components/sms/ContactSmsCard';

const STATUS_TONE = {
  new: 'bg-slate-100 text-slate-700 border-slate-300',
  contacted: 'bg-sky-100 text-sky-800 border-sky-300',
  qualified: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  not_a_fit: 'bg-rose-100 text-rose-700 border-rose-300',
  duplicate: 'bg-amber-100 text-amber-800 border-amber-300',
  converted: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  archived: 'bg-neutral-200 text-neutral-700 border-neutral-300',
};

export default function ProspectDrawer({ open, onOpenChange, assignmentId, event }) {
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [contacting, setContacting] = useState(false);

  const load = useCallback(async () => {
    if (!assignmentId) return;
    setLoading(true); setError(null);
    try {
      const r = await prospectAPI.byAssignment(assignmentId);
      setProspect(r.data);
    } catch (e) {
      const msg = e?.response?.status === 404
        ? 'No prospect linked to this registration.'
        : getErrorMessage(e, 'Failed to load prospect');
      setError(msg);
      setProspect(null);
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    if (open) { load(); setNote(''); }
  }, [open, load]);

  const contactForRecruiting = async () => {
    if (!prospect) return;
    setContacting(true);
    try {
      const eventTitle = event?.title || 'event';
      const body = note.trim()
        ? `Contacted for recruiting — ${note.trim()}\n(From event: ${eventTitle})`
        : `Contacted for recruiting from event: ${eventTitle}`;
      await prospectAPI.update(prospect.prospect_id, {
        status: prospect.status === 'new' ? 'contacted' : prospect.status,
        internal_note: body,
      });
      toast.success(
        prospect.status === 'new'
          ? 'Marked as Contacted — note saved in Prospects'
          : 'Note added to prospect — status unchanged',
      );
      setNote('');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not update prospect'));
    } finally {
      setContacting(false);
    }
  };

  const submitted = prospect?.submitted_at
    ? new Date(prospect.submitted_at).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : null;

  const prospectsUrl = event?.event_id
    ? `/prospects?source_event_id=${encodeURIComponent(event.event_id)}`
    : '/prospects';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="prospect-drawer">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-500" />
            Prospect details
          </SheetTitle>
          <SheetDescription>
            Recruiting profile captured from an event registration. Actions here
            sync directly with the Prospects section of the CRM.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading prospect…
          </div>
        ) : error ? (
          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        ) : prospect ? (
          <div className="space-y-5">
            {/* Header block */}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-slate-900" data-testid="prospect-name">
                  {prospect.first_name} {prospect.last_name}
                </h2>
                <Badge
                  variant="outline"
                  className={`border font-normal capitalize ${STATUS_TONE[prospect.status] || STATUS_TONE.new}`}
                  data-testid="prospect-status"
                >
                  {(prospect.status || 'new').replace(/_/g, ' ')}
                </Badge>
                {prospect.is_duplicate && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                    Duplicate
                  </Badge>
                )}
              </div>
              {submitted && (
                <p className="text-xs text-slate-500 mt-1">Captured on {submitted}</p>
              )}
            </div>

            {/* Contact info grid */}
            <div className="grid grid-cols-1 gap-2 border-t border-slate-100 pt-4">
              {prospect.email && (
                <Row icon={Mail} label="Email">
                  <a href={`mailto:${prospect.email}`} className="text-sky-600 hover:underline break-all">
                    {prospect.email}
                  </a>
                </Row>
              )}
              {prospect.phone && (
                <Row icon={Phone} label="Phone">
                  <a href={`tel:${prospect.phone}`} className="text-sky-600 hover:underline">
                    {formatPhone(prospect.phone)}
                  </a>
                </Row>
              )}
              {prospect.npn && (
                <Row icon={Hash} label="NPN">
                  <span className="font-mono text-slate-800">{prospect.npn}</span>
                </Row>
              )}
              {prospect.current_agency && (
                <Row icon={Building2} label="Current agency / company">
                  <span className="text-slate-800">{prospect.current_agency}</span>
                </Row>
              )}
              {prospect.source_event_title && (
                <Row icon={Calendar} label="Source event">
                  <span className="text-slate-800">{prospect.source_event_title}</span>
                </Row>
              )}
              {prospect.referred_by_name && (
                <Row icon={ShieldCheck} label="Referred by">
                  <span className="text-slate-800">
                    {prospect.referred_by_name}
                    {prospect.referred_by_npn ? ` · NPN ${prospect.referred_by_npn}` : ''}
                  </span>
                </Row>
              )}
            </div>

            {prospect.comments && (
              <div>
                <Label className="text-xs">Comments at signup</Label>
                <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1 p-2 rounded bg-slate-50 border border-slate-200">
                  {prospect.comments}
                </p>
              </div>
            )}

            {/* Recruiting action */}
            <div className="border-t border-slate-100 pt-4 space-y-2">
              <Label htmlFor="prospect-drawer-note" className="text-xs">
                Add a recruiting note (optional)
              </Label>
              <Textarea
                id="prospect-drawer-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Discussed AEP season, wants a follow-up call next week"
                data-testid="prospect-drawer-note"
              />
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600"
                onClick={contactForRecruiting}
                disabled={contacting}
                data-testid="prospect-drawer-contact-btn"
              >
                {contacting
                  ? <Loader2 size={14} className="mr-2 animate-spin" />
                  : <MessageSquarePlus size={14} className="mr-2" />}
                {prospect.status === 'new' ? 'Contact for recruiting' : 'Add follow-up note'}
              </Button>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {prospect.status === 'new'
                  ? 'Moves the prospect to "Contacted" and appends a note visible in the Prospects section.'
                  : 'Appends a note to the prospect record — status won\'t change.'}
              </p>
            </div>

            {/* Prospect notes history */}
            {Array.isArray(prospect.notes) && prospect.notes.length > 0 && (
              <div className="border-t border-slate-100 pt-4">
                <Label className="text-xs">Follow-up notes ({prospect.notes.length})</Label>
                <div className="space-y-2 mt-2 max-h-48 overflow-y-auto pr-1">
                  {[...prospect.notes]
                    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                    .map((n, i) => (
                      <div key={`${n.created_at || i}`} className="p-2 rounded border border-slate-200 bg-white">
                        <div className="text-[10px] text-slate-500 mb-0.5 flex justify-between">
                          <span>{n.author_name || 'KR staff'}</span>
                          <span>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                        </div>
                        <p className="text-xs text-slate-800 whitespace-pre-wrap">{n.body}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Link to Prospects section */}
            <div className="border-t border-slate-100 pt-4">
              <a
                href={prospectsUrl}
                className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1"
                data-testid="prospect-drawer-open-in-crm"
              >
                Open in Prospects section <ExternalLink size={12} />
              </a>
            </div>

            {/* SMS thread (Phase 27.37.B) — recruiters can text prospects
                directly from the drawer with the same consent + delivery
                rules as leads. */}
            {prospect.phone && (
              <div className="border-t border-slate-100 pt-4">
                <ContactSmsCard
                  contactType="prospect"
                  contactId={prospect.prospect_id}
                  phone={prospect.phone}
                />
              </div>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon size={13} className="text-slate-400 mt-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}
